import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Country } from '@tripointe/tripointe-cms/data-access';
import { Attendees, CountryValidationEnum } from '@tripointe/tripointe-cms/types';
import { isEqual } from 'lodash';

import { VALIDATION_PATTERNS } from './patterns';

export const VALIDATION_CONFIGS = {
  stringMaxLength: 1000,
  passMinLength: 8
};

export const VALIDATION_NAME_CONFIG = {
  nameMaxLength: 45
};

@Injectable({
  providedIn: 'root'
})
export class FormValidationService {
  constructor() {}

  isValidEmail(control: AbstractControl): ValidationErrors {
    const currentEmailValue = control.value;
    if (
      !currentEmailValue ||
      (currentEmailValue.length < VALIDATION_CONFIGS.stringMaxLength &&
        VALIDATION_PATTERNS.email.test(currentEmailValue))
    ) {
      return null;
    }

    return { email: true };
  }

  isValidPhone(control: AbstractControl): ValidationErrors {
    const currentPhoneValue = control.value;
    if (
      !currentPhoneValue ||
      currentPhoneValue.length === 0 ||
      (currentPhoneValue.length > 9 && currentPhoneValue.match(VALIDATION_PATTERNS.phone))
    ) {
      return null;
    }

    return { invalidPhone: true };
  }

  isValidName(control: AbstractControl): ValidationErrors {
    const currentName = control.value;
    if (currentName) {
      if (currentName.match(VALIDATION_PATTERNS.name)) {
        return null;
      }

      return { invalidName: true };
    }
  }

  isValidUSZipCode(control: AbstractControl): ValidationErrors {
    const currentZipCode = control.value;
    if (currentZipCode === '' || VALIDATION_PATTERNS.usaZipCode.test(currentZipCode)) {
      return null;
    }

    return { invalidUSZip: true };
  }

  isValidCAZipCode(control: AbstractControl): ValidationErrors {
    const currentZipCode = control.value;
    if (currentZipCode === '' || VALIDATION_PATTERNS.caZipCode.test(currentZipCode)) {
      return null;
    }

    return { invalidCAZip: true };
  }

  isValidPassword(control: AbstractControl): ValidationErrors {
    const currentPassword = control.value;

    if (
      currentPassword.length >= VALIDATION_CONFIGS.passMinLength &&
      currentPassword.length < VALIDATION_CONFIGS.stringMaxLength
    ) {
      return null;
    }

    return { password: true };
  }

  // The validation fn below requires controls to be named exactly as stated
  oldAndNewPassDifferent(formGroup: FormGroup): void {
    const oldPasswordControl = formGroup.get('currentPassword');
    const newPasswordControl = formGroup.get('password');
    const confirmPasswordControl = formGroup.get('passwordConfirmation');

    if (!oldPasswordControl.value && !newPasswordControl.value) return;

    // return if another validator has already found an error on the passwordControls
    if (newPasswordControl.errors && !newPasswordControl.errors.sameAsOld) {
      return;
    }

    if (newPasswordControl.value === oldPasswordControl.value) {
      newPasswordControl.setErrors({ sameAsOld: true });
      confirmPasswordControl.setErrors({ sameAsOld: true });
    } else {
      newPasswordControl.setErrors(null);
      confirmPasswordControl.setErrors(null);
    }
  }

  // The validation fn below requires controls to be named exactly as stated
  matchNewPasswords(formGroup: FormGroup): void {
    const newPasswordControl = formGroup.get('password');
    const confirmPasswordControl = formGroup.get('passwordConfirmation');

    if (!newPasswordControl.value && !confirmPasswordControl.value) {
      newPasswordControl.setErrors(null);
      confirmPasswordControl.setErrors(null);

      return;
    }

    // return if another validator has already found an error on the passwordControls
    if (newPasswordControl.errors && !newPasswordControl.errors.mustMatch) {
      return;
    }

    if (newPasswordControl.value !== confirmPasswordControl.value) {
      newPasswordControl.setErrors({ mustMatch: true });
      confirmPasswordControl.setErrors({ mustMatch: true });
    } else {
      newPasswordControl.setErrors(null);
      confirmPasswordControl.setErrors(null);
    }
  }

  // The validation fn below requires all controls to be filled
  checkCurrentPasswordFieldIsEntered(formGroup: FormGroup): void {
    const oldPasswordControl = formGroup.get('currentPassword');
    const newPasswordControl = formGroup.get('password');
    const confirmPasswordControl = formGroup.get('passwordConfirmation');
    const arePasswordsTouched =
      oldPasswordControl.touched || newPasswordControl.touched || confirmPasswordControl.touched;
    const arePasswordsEmpty = !newPasswordControl.value && !confirmPasswordControl.value;
    if (arePasswordsTouched && !oldPasswordControl.value) {
      oldPasswordControl.markAsTouched();
      oldPasswordControl.setErrors({ required: true });
    } else {
      oldPasswordControl.setErrors(null);
    }

    if (!oldPasswordControl.value && oldPasswordControl.touched) {
      oldPasswordControl.setErrors({ required: true });
    }
    if (arePasswordsEmpty) {
      oldPasswordControl.setErrors(null);
    }

    if (arePasswordsEmpty && arePasswordsTouched && !oldPasswordControl.value) {
      formGroup.reset();
    }

    // return if another validator has already found an error on the passwordControls
    if (newPasswordControl.errors && !newPasswordControl.errors.sameAsOld) {
      return;
    }
  }

  isValidPhaseCommunityAssignments(
    communityIdToDivisionId: Record<number, number>,
    phaseIdToDivisionId: Record<number, number>
  ): ValidatorFn {
    return (formGroup: FormGroup): ValidationErrors | null => {
      const { communitiesIds: communitiesIdsCtrl, phasesIds: phasesIdsCtrl } = formGroup.controls;
      const selectedCommunitiesIds: number[] = communitiesIdsCtrl.value;
      const selectedPhasesIds: number[] = phasesIdsCtrl.value;

      const expectedDivisionsIdsFromCommunities = [
        ...new Set(selectedCommunitiesIds.map(id => communityIdToDivisionId[id]))
      ];
      const expectedDivisionsIdsFromPhases = [
        ...new Set(selectedPhasesIds.map(id => phaseIdToDivisionId[id]))
      ];
      const isValid = isEqual(
        expectedDivisionsIdsFromCommunities.sort(),
        expectedDivisionsIdsFromPhases.sort()
      );

      if (!isValid) {
        phasesIdsCtrl.setErrors({ divisionMismatch: true });
      } else {
        phasesIdsCtrl.setErrors(selectedPhasesIds.length ? null : { required: true });
      }

      return null;
    };
  }

  isValidInviteesGroup(control: AbstractControl): ValidationErrors {
    const invitees = control.value;

    if (invitees) {
      const buyerSelected = invitees.some(invitee => {
        return invitee.includes(Attendees.buyer);
      });
      const contactSelected = invitees.some(invitee => {
        return invitee.includes(Attendees.teamMember);
      });

      if (!buyerSelected && !contactSelected) {
        return { required: true };
      }
      if (!buyerSelected) {
        return { buyersRequired: true };
      } else if (!contactSelected) {
        return { contactsRequired: true };
      }

      return null;
    }
  }

  isValidTimeRange(formGroup: FormGroup): void {
    const startTime = formGroup.get('start');
    const endTime = formGroup.get('end');

    if (!startTime.value || !endTime.value) {
      return;
    }

    if (startTime.value > endTime.value || startTime.value === endTime.value) {
      endTime.setErrors({ invalidRange: true });
    } else {
      endTime.setErrors(null);
    }
  }

  setNewZipCodeValidation(control: AbstractControl, country: Partial<Country>): void {
    control.setValidators([
      Validators.required,
      Validators.pattern(VALIDATION_PATTERNS[CountryValidationEnum[country.label]])
    ]);
  }
}
